import React, { useEffect, useRef, useContext, useState } from 'react';
import styled from 'styled-components'
import _ from 'lodash'
import useDimensions from '../hooks/useDimensions';
// import usePrevious from '../hooks/usePrevious';

import { select, mouse } from 'd3-selection'
import { stack } from 'd3-shape'
import { scaleLinear } from 'd3-scale'
import numeral from "numeral"

import { ChartContext } from '../context/ChartContext';
import { colorTeal, colorPurple, fontWeightXS, fontSizeS, fontWeightL, ColorGreyLight } from '../styles/sharedStyles';
import { numberTween } from '../../../chartFunctions';
import Tooltip from './Tooltip';

const ChartWrapper = styled.div`
    position: relative;
    height: 100%;
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: stretch;
`

const SvgWrapper = styled.div`
    border-radius: 3px;
    overflow: hidden;

    height: 60%;
    width: 100%;

    svg {
        height: 100%;
        width: 100%;

        text {
            fill: #fff;
            text-anchor: middle;
            pointer-events: none;
            user-select: none;
        }
    }
`

let xScale

const BarChart = ({ 
    data ,
    dataCompare
}) => {
    const { path, hideFilters, filteredAnswers } = useContext(ChartContext)
    const [ mousePosition, setMousePosition ] = useState('')
    const [ hoveredData, setHoveredData ] = useState('')
    const color = path === "hrds" ? colorTeal : colorPurple
    
    let initVis, updateData, updateDims
    const value = _.max(Object.values(data))
    const svgRef = useRef()
    const wrapperRef = useRef()
    const init = useRef()
    const svg = select(svgRef.current)
    const { width, height } = useDimensions(svgRef.current, hideFilters)
    // const prevWidth = usePrevious(width)
    useEffect(() => init.current && updateData(),[updateData, value, filteredAnswers.length])
    useEffect(() => init.current && updateDims(),[width, height, updateDims])
    useEffect(() => {
        if(!init.current && (width && height)){
            initVis()
            init.current = true
        }}, [initVis, width, height])

    initVis = () => {
        const stackData = getStackData([data])
        xScale = scaleLinear().domain([0,1]).range([0, width])
        createUpdateRects(stackData)
        createUpdateAnswerTexts(stackData, "answer")
        createUpdateNumberTexts(stackData, "value")
    }

    updateData = () => {
        const stackData = getStackData([data])
        createUpdateRects(stackData)
        createUpdateAnswerTexts(stackData, "answer")
        createUpdateNumberTexts(stackData, "value")
    }

    updateDims = () => {
        xScale.range([0, width])
        svg.selectAll("rect")
            .attr("x", d => xScale(d[0][0]))
            .attr("width", d => xScale(d[0][1]) - xScale(d[0][0]))
        svg.selectAll("text")
            .attr("x", d => xScale(d[0][0] + (d[0][1] - d[0][0])/2))
            .each((d,i,n) => hideOrShow(d,i,n,xScale))
    }

    const getStackData = raw => stack().keys(Object.keys(raw[0]))(raw)

    const createUpdateRects = data => {

        const rects = svg.selectAll("rect").data(data)
        rects.enter()
            .append("rect")
            .attr("fill", d => d.key === "Missing answer" ? ColorGreyLight : color)
            .attr("stroke", "#fff")
            .attr("stroke-width", .5)
            .attr("y", 0)
            .attr("height", height)
            .attr("x", d => xScale(d[0][0]))
            .attr("width", d => xScale(d[0][1] - d[0][0]))
            .on("mousemove", () => {
                const mousePos = mouse(svgRef.current)
                setMousePosition(mousePos)
            })
            .on('mouseover', d => {
                d.key !== hoveredData.key && setHoveredData(d)
            })
            .on('mouseout', () => setMousePosition([0,0]))
                .merge(rects)
                .transition('update')
                .duration(1000)
                .attr("x", d => xScale(d[0][0]))
                .attr("width", d => xScale(d[0][1] - d[0][0]))

    }

    const createUpdateAnswerTexts = (data) => {

        const texts = svg.selectAll(".answer-text").data(data)

        texts.enter()
            .append("text")
            .attr("class", "answer-text" )
            .attr("y", height * .35)
            .attr("x", d => xScale(d[0][0] + (d[0][1] - d[0][0])/2))
            .attr("font-size", fontSizeS)
            .attr("font-weight", fontWeightXS)
            .text(d => d.key )
                .merge(texts)
                .transition('update')
                .duration(1000)
                .attr("x", d => xScale(d[0][0] + (d[0][1] - d[0][0])/2))
                .text(d => d.key)
                .each((d,i,n) => hideOrShow(d,i,n,xScale))

    }

    const createUpdateNumberTexts = (data) => {

        const texts = svg.selectAll(".value-text").data(data)

        texts.enter()
            .append("text")
            .attr("class", "value-text" )
            .attr("y", height * .75)
            .attr("x", d => xScale(d[0][0] + (d[0][1] - d[0][0])/2))
            .attr("font-size", fontSizeS)
            .attr("font-weight", fontWeightL)
            .text(d => numeral(d[0][1] - d[0][0]).format('0.0%') )
                .merge(texts)
                .transition('update')
                .duration(1000)
                .attr("x", d => xScale(d[0][0] + (d[0][1] - d[0][0])/2))
                .tween('text', (d,i,n) => numberTween(d,i,n, "0.0%"))
                .each((d,i,n) => hideOrShow(d,i,n,xScale))

    }
    const svgContainer = wrapperRef.current && wrapperRef.current.getBoundingClientRect()
    return ( 
        <ChartWrapper
            ref={wrapperRef}
        >
            <SvgWrapper>
                <svg 
                    ref={svgRef}
                />
            </SvgWrapper>
            <ChartTooltip
                mousePosition={mousePosition}
                hoveredData={hoveredData}
                data={data}
                compare={dataCompare}
                filteredValues={filteredAnswers}
                parentWidth={svgContainer && svgContainer.width}
            />
        </ChartWrapper>
     );
}
 
export default BarChart;

const ChartTooltip = ({
    mousePosition,
    hoveredData,
    data,
    compare,
    filteredValues,
    parentWidth
}) => {
    const { hideFilters } = useContext(ChartContext)
    const tooltipRef = useRef()
    const tooltipDims = tooltipRef.current && tooltipRef.current.getBoundingClientRect()
    const visibility = mousePosition && !mousePosition.includes(0)
    const key = hoveredData &&  hoveredData.key
    const value = data[key]
    const compareValue = compare[key]
    const difference = value - compareValue
    const orientation = !hideFilters ? "left" : mousePosition[0] < parentWidth/2 ? 'right' : 'left'
    const leftPosition = orientation === 'left'
                    ? mousePosition[0] - (tooltipDims && tooltipDims.width) - 15
                    : mousePosition[0] + 15

    return(
        <Tooltip
            ref={tooltipRef}
            position={orientation}
            top={mousePosition[1] - 10}
            left={leftPosition}
            visible={visibility}
        >
            <div className="title">
                {key}
            </div>
            <div className="value">
                <div className="value_value">
                    {numeral(value).format("0.00%")}
                </div>
                { filteredValues.length > 0 &&
                    <div className="value_text">
                       &nbsp;({numeral(difference).format("+0.00%")} compared to all respondents)
                    </div>
                }
            </div>
        </Tooltip>
    )
}


function hideOrShow(d, i, n, scale) {
    var self = select(n[i]),
        textWidth = self.node().getComputedTextLength(),
        parentWidth = scale(d[0][1] - d[0][0])
    
    textWidth > (parentWidth * .95) || d.key === "Missing answer"
        ? self.transition('hide').duration(300).attr('opacity', 0) 
        : self.transition('show').duration(300).attr('opacity', 1)

  }



